import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "components/drawerSlide/drawer";
import DropFilter from "components/dropdown/DropFilter";
import { Select } from "components/select/index";
import { TGetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { TCustomerType } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import "./accountsFilterDrawer.scss";

export type AccountsDrawerFilters = Pick<
  TGetAccount,
  | "customer_type_id"
  | "cart_customer_type_id"
  | "league_id"
  | "tournament_id"
  | "has_default_payment_method"
  | "closed"
>;

function eventYearLabel(dateValue?: string | null): string {
  if (!dateValue) {
    return "";
  }
  const year = new Date(dateValue).getFullYear();
  return Number.isFinite(year) ? ` (${year})` : "";
}

function asSingleId(value?: number | number[]): number | undefined {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : undefined;
  }
  return value;
}

export function emptyAccountsDrawerFilters(): AccountsDrawerFilters {
  return {
    customer_type_id: [],
    cart_customer_type_id: [],
    league_id: undefined,
    tournament_id: undefined,
    has_default_payment_method: undefined,
    closed: false,
  };
}

export function countActiveAccountsDrawerFilters(filters: AccountsDrawerFilters): number {
  let count = 0;
  if ((filters.customer_type_id?.length ?? 0) > 0) {
    count += 1;
  }
  if ((filters.cart_customer_type_id?.length ?? 0) > 0) {
    count += 1;
  }
  if (filters.league_id) {
    count += 1;
  }
  if (filters.tournament_id) {
    count += 1;
  }
  if (typeof filters.has_default_payment_method === "boolean") {
    count += 1;
  }
  // Default is open-only (`closed: false`); Closed-only or Any count as active.
  if (filters.closed !== false) {
    count += 1;
  }
  return count;
}

function toDrawerFilters(filters: TGetAccount): AccountsDrawerFilters {
  return {
    customer_type_id: filters.customer_type_id ?? [],
    cart_customer_type_id: filters.cart_customer_type_id ?? [],
    league_id: filters.league_id,
    tournament_id: filters.tournament_id,
    has_default_payment_method: filters.has_default_payment_method,
    closed: filters.closed,
  };
}

type Props = {
  open: boolean;
  appliedFilters: TGetAccount;
  customerTypes: TCustomerType[];
  leagues: ILeague[];
  tournaments: ITournament[];
  leagueSearching: boolean;
  tournamentSearching: boolean;
  onLeagueSearch: (query: string) => void;
  onTournamentSearch: (query: string) => void;
  onClose: () => void;
  onApply: (filters: AccountsDrawerFilters) => void;
};

export default function AccountsFilterDrawer(props: Props) {
  const {
    open,
    appliedFilters,
    customerTypes,
    leagues,
    tournaments,
    leagueSearching,
    tournamentSearching,
    onLeagueSearch,
    onTournamentSearch,
    onClose,
    onApply,
  } = props;
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AccountsDrawerFilters>(emptyAccountsDrawerFilters());
  const [draftKey, setDraftKey] = useState(0);

  useEffect(() => {
    if (open) {
      setDraft(toDrawerFilters(appliedFilters));
      setDraftKey(prev => prev + 1);
    }
  }, [open, appliedFilters]);

  const activeFilterCount = countActiveAccountsDrawerFilters(draft);

  function handleClearAll() {
    setDraft(emptyAccountsDrawerFilters());
    setDraftKey(prev => prev + 1);
    onLeagueSearch("");
    onTournamentSearch("");
  }

  function handleApply() {
    onApply(draft);
    onClose();
  }

  const greenFeeTypes = customerTypes.filter(type => type.application === "green_fee");
  const cartTypes = customerTypes.filter(type => type.application === "power_cart");

  return (
    <div className="accounts-filter-drawer">
      <Drawer
        open={open}
        title={t("Filters")}
        subtitle={t("Apply filters to the accounts table.")}
        onClose={onClose}
        onOk={handleApply}
        okText={
          activeFilterCount > 0
            ? t("Apply {{count}} filters", { count: activeFilterCount })
            : t("Apply filters")
        }
        showCancel
        cancelText={t("Cancel")}
        cancelButtonType="text"
        secondaryActions={[{ label: t("Clear all"), action: handleClearAll, buttonType: "secondary" }]}
        backDropClose
      >
        {greenFeeTypes.length > 0 ? (
          <Drawer.Section bordered>
            <DropFilter
              key={`customer-type-${draftKey}`}
              title={t("Customer Type")}
              filterData={greenFeeTypes}
              filterLabelPropFromData="title"
              filterIdPropFromData="id"
              filterType="Checkbox"
              applyFilters={filterReturn =>
                setDraft(prev => ({
                  ...prev,
                  customer_type_id: filterReturn.map(filter => filter.id as number),
                }))
              }
              defaultCheckboxes={draft.customer_type_id}
              leftAligned
              subtitle
            />
          </Drawer.Section>
        ) : null}

        {cartTypes.length > 0 ? (
          <Drawer.Section bordered>
            <DropFilter
              key={`cart-type-${draftKey}`}
              title={t("Cart Type")}
              filterData={cartTypes}
              filterLabelPropFromData="title"
              filterIdPropFromData="id"
              filterType="Checkbox"
              applyFilters={filterReturn =>
                setDraft(prev => ({
                  ...prev,
                  cart_customer_type_id: filterReturn.map(filter => filter.id as number),
                }))
              }
              defaultCheckboxes={draft.cart_customer_type_id}
              leftAligned
            />
          </Drawer.Section>
        ) : null}

        <Drawer.Section bordered>
          <Select
            key={`league-${draftKey}-${asSingleId(draft.league_id) ?? "any"}`}
            label={t("League Roster")}
            defaultValue={asSingleId(draft.league_id) ?? ""}
            showSearch
            showDropDownOnFocus
            searching={leagueSearching}
            placeholder={t("Search leagues or year...")}
            onSearch={(query: string) => onLeagueSearch(query)}
            onChange={(value: string | number) => {
              onLeagueSearch("");
              setDraft(prev => ({
                ...prev,
                league_id: value === "" || value == null ? undefined : Number(value),
              }));
            }}
          >
            <Select.Option value="" name={t("Any league")}>
              <span>{t("Any league")}</span>
            </Select.Option>
            {leagues.map(league => (
              <Select.Option
                key={league.id}
                value={league.id}
                name={`${league.name}${eventYearLabel(league.start_date)}`}
              >
                <span>
                  {league.name}
                  {eventYearLabel(league.start_date)}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Drawer.Section>

        <Drawer.Section bordered>
          <Select
            key={`tournament-${draftKey}-${asSingleId(draft.tournament_id) ?? "any"}`}
            label={t("Tournament Roster")}
            defaultValue={asSingleId(draft.tournament_id) ?? ""}
            showSearch
            showDropDownOnFocus
            searching={tournamentSearching}
            placeholder={t("Search tournaments or year...")}
            onSearch={(query: string) => onTournamentSearch(query)}
            onChange={(value: string | number) => {
              onTournamentSearch("");
              setDraft(prev => ({
                ...prev,
                tournament_id: value === "" || value == null ? undefined : Number(value),
              }));
            }}
          >
            <Select.Option value="" name={t("Any tournament")}>
              <span>{t("Any tournament")}</span>
            </Select.Option>
            {tournaments.map(tournament => (
              <Select.Option
                key={tournament.id}
                value={tournament.id}
                name={`${tournament.name}${eventYearLabel(tournament.date)}`}
              >
                <span>
                  {tournament.name}
                  {eventYearLabel(tournament.date)}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Drawer.Section>

        <Drawer.Section bordered>
          <Select
            key={`pm-${draftKey}-${
              typeof draft.has_default_payment_method === "boolean"
                ? draft.has_default_payment_method
                  ? "1"
                  : "0"
                : "any"
            }`}
            label={t("Has Payment Method")}
            defaultValue={
              typeof draft.has_default_payment_method === "boolean"
                ? draft.has_default_payment_method
                  ? 1
                  : 0
                : ""
            }
            onChange={(value: string | number) => {
              if (value === "" || value == null) {
                setDraft(prev => ({ ...prev, has_default_payment_method: undefined }));
                return;
              }
              setDraft(prev => ({ ...prev, has_default_payment_method: Number(value) === 1 }));
            }}
          >
            <Select.Option value="" name={t("Any")}>
              <span>{t("Any")}</span>
            </Select.Option>
            <Select.Option value={1} name={t("Yes")}>
              <span>{t("Yes")}</span>
            </Select.Option>
            <Select.Option value={0} name={t("No")}>
              <span>{t("No")}</span>
            </Select.Option>
          </Select>
        </Drawer.Section>

        <Drawer.Section>
          <Select
            key={`closed-${draftKey}-${typeof draft.closed === "boolean" ? (draft.closed ? "1" : "0") : "any"}`}
            label={t("Closed")}
            defaultValue={typeof draft.closed === "boolean" ? (draft.closed ? 1 : 0) : ""}
            onChange={(value: string | number) => {
              if (value === "" || value == null) {
                setDraft(prev => ({ ...prev, closed: undefined }));
                return;
              }
              setDraft(prev => ({ ...prev, closed: Number(value) === 1 }));
            }}
          >
            <Select.Option value={0} name={t("Open")}>
              <span>{t("Open")}</span>
            </Select.Option>
            <Select.Option value={1} name={t("Closed")}>
              <span>{t("Closed")}</span>
            </Select.Option>
            <Select.Option value="" name={t("Any")}>
              <span>{t("Any")}</span>
            </Select.Option>
          </Select>
        </Drawer.Section>
      </Drawer>
    </div>
  );
}
